import {
	getDatabase,
	ref,
	runTransaction,
	onValue,
	onDisconnect,
	Unsubscribe,
} from 'firebase/database';
import { ILoginState } from 'global-stores';

export interface OnboardingLock {
	userId: string;
	userName: string;
    userEmail: string;
	timestamp: number;
}

/**
 * Duration (ms) after which a lock is considered expired.
 */
export const LOCK_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Attempts to acquire a lock for the given onboarding flow.
 * Only one user can hold the lock at a time. If the lock is expired, it can be taken over.
 * @returns { success, lock, error }
 */
export const acquireOnboardingLock = async (
	db: ReturnType<typeof getDatabase>,
	flowId: string,
	user: ILoginState
): Promise<{ success: boolean; lock?: OnboardingLock; error?: any }> => {
	const lockRef = ref(db, `onboardingLocks/${flowId}`);
	const now = Date.now();
	let acquired = false;
	let lock: OnboardingLock | undefined;
	try {
		await runTransaction(lockRef, current => {
			if (!current || now - current.timestamp > LOCK_EXPIRY_MS) {
				acquired = true;
				lock = {
					userId: user.userId || '',
					userName: user.name || '',
                    userEmail: user.email || '',
					timestamp: now,
				};
				return lock;
			}
			lock = current;
			return;
		});
		return { success: acquired, lock };
	} catch (error) {
		return { success: false, error };
	}
};

/**
 * Releases the lock for the given onboarding flow, but only if the current user owns it.
 * Uses a transaction for atomicity.
 */
export const releaseOnboardingLock = async (
	db: ReturnType<typeof getDatabase>,
	flowId: string,
	userId: string
) => {
	const lockRef = ref(db, `onboardingLocks/${flowId}`);
	await runTransaction(lockRef, current => {
		if (current && current.userId === userId) {
			return null; // Remove the lock
		}
		return current; // Do nothing if not owner
	});
};

/**
 * Listens for changes to the onboarding lock for a given flow.
 */
export const listenOnboardingLock = (
	db: ReturnType<typeof getDatabase>,
	flowId: string,
	callback: (lock: OnboardingLock | null) => void
): Unsubscribe => {
	const lockRef = ref(db, `onboardingLocks/${flowId}`);
	return onValue(lockRef, snapshot => {
		callback(snapshot.val());
	});
};

/**
 * Sets up automatic lock removal on disconnect for the current client.
 */
export const setOnboardingLockOnDisconnect = (
	db: ReturnType<typeof getDatabase>,
	flowId: string
) => {
	const lockRef = ref(db, `onboardingLocks/${flowId}`);
	onDisconnect(lockRef).remove();
};

/**
 * Updates the lock's timestamp (heartbeat), but only if the current user owns the lock.
 * Uses a transaction for safety.
 */
export const updateOnboardingLockHeartbeat = async (
	db: ReturnType<typeof getDatabase>,
	flowId: string,
	user: ILoginState
) => {
	const lockRef = ref(db, `onboardingLocks/${flowId}`);
	await runTransaction(lockRef, current => {
		if (current && current.userId === user.userId) {
			return { ...current, timestamp: Date.now() };
		}
		return current;
	});
};
